/**
 * The outline of a long answer.
 *
 * A lecture runs to several thousand words across a dozen sections, and
 * scrolling is a poor way to find "the part about boundary conditions" an hour
 * later. This reads the headings the tutor actually wrote and turns them into a
 * contents list that tracks where you are.
 *
 * It appears only when there is enough structure to be worth it — an outline
 * over two headings is furniture, not navigation.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { List } from "lucide-react";
import { normalizeMath } from "../markdown/normalize-math.ts";
import { MathText } from "../markdown/MathText.tsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible.tsx";
import { cn } from "../lib/utils.ts";

/** Below this an answer is short enough to simply read. */
const MIN_HEADINGS = 3;

export interface OutlineEntry {
  readonly id: string;
  readonly text: string;
  readonly level: number;
}

interface AnswerOutlineProps {
  readonly entries: readonly OutlineEntry[];
  readonly activeId: string | null;
  /** The rendered answer, searched for the headings to scroll to. */
  readonly bodyRef: React.RefObject<HTMLElement | null>;
}

/**
 * The outline's state, shared.
 *
 * Both the contents list and the sticky heading need the same headings and the
 * same notion of "where am I" — computing it twice would mean two scroll
 * listeners doing identical work on every frame.
 */
export function useOutline(
  content: string,
  bodyRef: React.RefObject<HTMLElement | null>,
): { entries: readonly OutlineEntry[]; activeId: string | null } {
  const entries = useMemo(() => extractHeadings(content), [content]);
  const activeId = useActiveHeading(entries, bodyRef);
  return { entries, activeId };
}

/** True when there is enough structure for navigation to be worth showing. */
export const hasOutline = (entries: readonly OutlineEntry[]): boolean =>
  entries.length >= MIN_HEADINGS;

export function AnswerOutline({ entries, activeId, bodyRef }: AnswerOutlineProps) {
  const [open, setOpen] = useState(false);

  if (!hasOutline(entries)) return null;

  const jump = (entry: OutlineEntry) => {
    const heading = findHeading(bodyRef.current, entry);
    heading?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="mb-5 rounded-md border bg-muted font-sans"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-secondary-foreground">
        <List className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="flex-1">
          {entries.length} sections
          {!open && activeIndex(entries, activeId) >= 0 && (
            <span className="text-muted-foreground">
              {" · reading "}
              {activeIndex(entries, activeId) + 1}
            </span>
          )}
        </span>
        <span className="text-xs text-primary">
          {open ? "hide contents" : "contents"}
        </span>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <nav aria-label="Contents" className="px-3 pb-3">
          <ol className="grid gap-0.5 text-sm">
            {entries.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => jump(entry)}
                  style={{ paddingLeft: `${(entry.level - 1) * 0.85}rem` }}
                  className={cn(
                    "w-full rounded-sm py-1 pr-2 text-left transition-colors",
                    entry.id === activeId
                      ? "font-medium text-foreground"
                      : "text-muted-foreground hover:text-secondary-foreground",
                  )}
                  aria-current={entry.id === activeId ? "location" : undefined}
                >
                  <MathText children={entry.text} />
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </CollapsibleContent>
    </Collapsible>
  );
}

const activeIndex = (entries: readonly OutlineEntry[], id: string | null) =>
  entries.findIndex((entry) => entry.id === id);

/* -------------------------------------------------------------------------- */
/* Reading the headings                                                        */
/* -------------------------------------------------------------------------- */

const HEADING = /^(#{1,4})[ \t]+(.+?)[ \t]*#*$/gm;

/**
 * Pull headings from the markdown rather than the DOM.
 *
 * The source is authoritative and available before render, and it avoids a
 * re-query on every streamed token. Fenced blocks are skipped so a `#` comment
 * inside a code sample is not mistaken for a section.
 */
export function extractHeadings(markdown: string): readonly OutlineEntry[] {
  const prose = stripFences(normalizeMath(markdown));
  const entries: OutlineEntry[] = [];
  const seen = new Map<string, number>();

  HEADING.lastIndex = 0;
  let match = HEADING.exec(prose);

  while (match !== null) {
    const level = match[1]!.length;
    const text = match[2]!.trim();

    if (text.length > 0) {
      // Duplicate headings are common ("Setup" twice); the suffix keeps their
      // ids distinct so scrolling lands on the right one.
      const base = slug(text);
      const count = seen.get(base) ?? 0;
      seen.set(base, count + 1);

      entries.push({
        id: count === 0 ? base : `${base}-${count}`,
        text,
        level,
      });
    }
    match = HEADING.exec(prose);
  }

  return entries;
}

/**
 * Drop fenced blocks before scanning for headings.
 *
 * Line-based rather than a regex: a lazy `[\s\S]*?` paired with `$` under the
 * `m` flag terminates at the first line ending, which left every fence body in
 * place and turned shell comments into phantom sections.
 */
function stripFences(markdown: string): string {
  const kept: string[] = [];
  let fence: string | null = null;

  for (const line of markdown.split("\n")) {
    const marker = /^[ \t]*(`{3,}|~{3,})/.exec(line)?.[1] ?? null;

    if (fence !== null) {
      // A closing fence must use the same character and be at least as long.
      if (marker && marker[0] === fence[0] && marker.length >= fence.length) {
        fence = null;
      }
      continue;
    }

    if (marker) {
      fence = marker;
      continue;
    }
    kept.push(line);
  }

  return kept.join("\n");
}

function slug(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/\$[^$]*\$/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section"
  );
}

/* -------------------------------------------------------------------------- */
/* Tracking position                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Which section the reader is in.
 *
 * Uses scroll position against each heading's offset rather than
 * IntersectionObserver: the answer grows while it streams, and an observer
 * would need re-registering on every token.
 */
function useActiveHeading(
  entries: readonly OutlineEntry[],
  bodyRef: React.RefObject<HTMLElement | null>,
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (entries.length < MIN_HEADINGS) return;

    const body = bodyRef.current;
    const scroller = findScroller(body);
    if (!scroller) return;

    const update = () => {
      frame.current = null;
      const headings = [...(body?.querySelectorAll("h1,h2,h3,h4") ?? [])];
      if (headings.length === 0) return;

      const top = scroller.getBoundingClientRect().top + 80;
      let current: string | null = entries[0]!.id;

      for (let i = 0; i < headings.length && i < entries.length; i++) {
        if (headings[i]!.getBoundingClientRect().top <= top) {
          current = entries[i]!.id;
        }
      }
      setActiveId(current);
    };

    const onScroll = () => {
      // Coalesced to one update per frame; scroll fires far faster than paint.
      if (frame.current === null) frame.current = requestAnimationFrame(update);
    };

    update();
    scroller.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [entries, bodyRef]);

  return activeId;
}

/** The nearest ancestor that actually scrolls. */
function findScroller(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;

  while (current) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === "auto" || overflow === "scroll") return current;
    current = current.parentElement;
  }
  return null;
}

/** Match an outline entry back to its rendered heading, by position. */
function findHeading(
  body: HTMLElement | null,
  entry: OutlineEntry,
): HTMLElement | null {
  const headings = [...(body?.querySelectorAll("h1,h2,h3,h4") ?? [])];
  const index = headings.findIndex(
    (node) => (node.textContent ?? "").trim() === entry.text.replace(/\$/g, ""),
  );

  return (headings[index] ?? null) as HTMLElement | null;
}
